import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import api from "../utils/api";
import { Shield, UserPlus, Trash2, Edit2, CheckCircle, XCircle } from "lucide-react";

const StaffManagement = () => {
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    username: "",
    password: "",
    role: "waiter",
  });

  const fetchStaff = async () => {
    try {
      setLoading(true);
      const res = await api.get("staff");
      if (res.data && res.data.success) {
        setStaffList(res.data.data);
      }
    } catch (error) {
      console.error("Failed to load staff list:", error);
      toast.error("Failed to load staff members");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStaff();
  }, []);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const resetForm = () => {
    setFormData({
      name: "",
      username: "",
      password: "",
      role: "waiter",
    });
    setEditingStaff(null);
  };

  const handleEditClick = (staff) => {
    setEditingStaff(staff);
    setFormData({
      name: staff.name,
      username: staff.username,
      password: "", // Leave password blank unless updating
      role: staff.role,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.username.trim() || !formData.role) {
      toast.error("Please fill in all required fields.");
      return;
    }

    if (!editingStaff && !formData.password) {
      toast.error("Password is required for new staff accounts.");
      return;
    }

    setSubmitting(true);
    try {
      if (editingStaff) {
        // Update Staff
        const res = await api.put(`staff/${editingStaff._id}`, formData);
        if (res.data && res.data.success) {
          toast.success("Staff member updated successfully!");
          fetchStaff();
          resetForm();
        }
      } else {
        // Create Staff
        const res = await api.post("staff", formData);
        if (res.data && res.data.success) {
          toast.success("Staff member added successfully!");
          fetchStaff();
          resetForm();
        }
      }
    } catch (error) {
      console.error("Staff submit error:", error);
      const errorMsg = error.response?.data?.message || "Operation failed. Please try again.";
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (staff) => {
    try {
      const res = await api.put(`staff/${staff._id}`, {
        isActive: !staff.isActive,
      });
      if (res.data && res.data.success) {
        toast.success(`Staff status updated successfully!`);
        fetchStaff();
      }
    } catch (error) {
      console.error("Failed to toggle status:", error);
      toast.error("Failed to update status");
    }
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This staff member will be permanently deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await api.delete(`staff/${id}`);
          if (res.data && res.data.success) {
            toast.success("Staff member deleted successfully!");
            fetchStaff();
          }
        } catch (error) {
          console.error("Failed to delete staff:", error);
          toast.error("Failed to delete staff member");
        }
      }
    });
  };

  return (
    <div className="container mx-auto px-4 mt-20 pb-10">
      <ToastContainer />
      <div className="flex flex-col md:flex-row gap-6">
        {/* Form Panel */}
        <div className="w-full md:w-1/3 bg-white p-6 rounded-2xl shadow-lg border border-gray-100 self-start">
          <div className="flex items-center gap-2 mb-6 text-teal-700">
            <UserPlus className="w-6 h-6" />
            <h2 className="text-xl font-bold font-serif">
              {editingStaff ? "Edit Staff Account" : "Add Staff Account"}
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-gray-700 text-sm font-semibold mb-1">
                Full Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="E.g. Rahul Singh"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                required
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-semibold mb-1">
                Username * (Used for login)
              </label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                placeholder="E.g. rahul_waiter"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                required
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-semibold mb-1">
                Password {editingStaff ? "(Leave blank to keep current)" : "*"}
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="••••••••"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                required={!editingStaff}
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-semibold mb-1">
                Staff Role *
              </label>
              <select
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                required
              >
                <option value="waiter">Waiter (Orders & Table management)</option>
                <option value="chef">Chef (Kitchen display order queue)</option>
                <option value="cashier">Cashier (Billing, CRM, History)</option>
                <option value="manager">Manager (Menu and complete operational access)</option>
              </select>
            </div>

            <div className="flex gap-2 pt-2">
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 bg-teal-600 hover:bg-teal-700 text-white font-bold py-2.5 px-4 rounded-lg shadow transition duration-200 disabled:opacity-50"
              >
                {submitting ? "Saving..." : editingStaff ? "Update Staff" : "Add Staff"}
              </button>
              {editingStaff && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-2.5 px-4 rounded-lg transition duration-200"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Directory Panel */}
        <div className="w-full md:w-2/3 bg-white p-6 rounded-2xl shadow-lg border border-gray-100">
          <div className="flex items-center gap-2 mb-6 text-teal-700">
            <Shield className="w-6 h-6" />
            <h2 className="text-xl font-bold font-serif">Staff Directory</h2>
          </div>

          {loading ? (
            <div className="text-center py-10 text-gray-500">Loading staff directory...</div>
          ) : staffList.length === 0 ? (
            <div className="text-center py-10 text-gray-500">
              No staff accounts configured. Create accounts using the form on the left.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Username
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {staffList.map((staff) => (
                    <tr key={staff._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {staff.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {staff.username}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                        {staff.role}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => handleToggleActive(staff)}
                          title="Click to toggle status"
                          className="flex items-center gap-1.5 focus:outline-none"
                        >
                          {staff.isActive ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <CheckCircle className="w-3.5 h-3.5 mr-1" /> Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              <XCircle className="w-3.5 h-3.5 mr-1" /> Inactive
                            </span>
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                        <div className="flex justify-center gap-3">
                          <button
                            onClick={() => handleEditClick(staff)}
                            className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-1.5 rounded-lg transition-colors"
                            title="Edit Staff member"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(staff._id)}
                            className="text-red-600 hover:text-red-800 hover:bg-red-50 p-1.5 rounded-lg transition-colors"
                            title="Delete Staff member"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StaffManagement;
